import { useId, useState, type FormEvent } from 'react';
import { t, type Idioma } from '@matias-asset-os/i18n';

interface Props {
  etiqueta: string;
  /** Que va a ocurrir, en una frase que se pueda leer entera antes de decidir. */
  consecuencias: string;
  idioma: Idioma;
  pideMotivo?: boolean;
  /** Etiqueta del campo de fecha. Si falta, no se pide fecha. */
  etiquetaFecha?: string;
  deshabilitado?: boolean;
  onConfirmar: (datos: { motivo: string; fecha: string }) => void;
}

/**
 * Accion con consecuencias: nunca se ejecuta al primer clic.
 *
 * El primer clic abre el detalle de lo que va a pasar y pide lo que haga falta
 * —motivo, fecha—; el segundo confirma. No se usa el dialogo del navegador
 * porque ahi no caben tres lineas de consecuencias ni un campo de fecha, y
 * porque un `confirm()` se acepta por reflejo sin leerlo.
 */
export function AccionConfirmada({
  etiqueta,
  consecuencias,
  idioma,
  pideMotivo = false,
  etiquetaFecha,
  deshabilitado = false,
  onConfirmar,
}: Props) {
  const ids = { motivo: useId(), fecha: useId() };
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  // La fecha efectiva se propone hoy, pero se puede corregir: un contrato puede
  // haber terminado el ultimo dia del mes y registrarse tres dias despues.
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));

  const confirmar = (evento: FormEvent) => {
    evento.preventDefault();
    onConfirmar({ motivo: motivo.trim(), fecha });
    setAbierto(false);
    setMotivo('');
  };

  if (!abierto) {
    return (
      <button
        type="button"
        className="secundario"
        disabled={deshabilitado}
        onClick={() => setAbierto(true)}
      >
        {etiqueta}
      </button>
    );
  }

  return (
    <form className="formulario confirmacion" onSubmit={confirmar}>
      <p>
        <strong>{etiqueta}</strong>
      </p>
      <p>{t(idioma, 'app.consecuencias')}</p>
      <p>{consecuencias}</p>

      {etiquetaFecha && (
        <div className="campo">
          <label htmlFor={ids.fecha}>{etiquetaFecha}</label>
          <input
            id={ids.fecha}
            type="date"
            required
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        </div>
      )}

      {pideMotivo && (
        <div className="campo">
          <label htmlFor={ids.motivo}>{t(idioma, 'cicloVida.motivo')}</label>
          <input
            id={ids.motivo}
            required
            minLength={3}
            maxLength={300}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
      )}

      <div className="acciones">
        <button type="submit">{t(idioma, 'app.confirmar')}</button>
        <button type="button" className="secundario" onClick={() => setAbierto(false)}>
          {t(idioma, 'app.cancelar')}
        </button>
      </div>
    </form>
  );
}
